import { useState, useRef, useEffect } from 'react'
import css from './timePicker.module.css'
import ClockCircle from '../../../assets/icons/clockCircle.svg?react'
import SwapRight from '../../../assets/icons/swapRight.svg?react'
import TimeDropdown from '../TimeDropdown'
import usePopup from '@/shared/hooks/usePopup'
import cn from '@/utils/cn'

type IProps = {
  value?: string
  secondValue?: string
  range?: boolean
  disabled?: boolean
  type?: 'default' | 'itemCalendar'
  onChange?: (val: string | [string, string]) => void
}

const TimePicker = ({
  type = 'default',
  value = null,
  secondValue = null,
  disabled = false,
  range = false,
  onChange,
}: IProps) => {
  const [selectedStartTime, setSelectedStartTime] = useState(value)
  const [selectedEndTime, setSelectedEndTime] = useState(secondValue)
  const [isSelectingStartTime, setIsSelectingStartTime] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const { open, setOpen } = usePopup(containerRef)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (value !== selectedStartTime) setSelectedStartTime(value)
    if (secondValue !== selectedEndTime) setSelectedEndTime(secondValue)
  }, [value, secondValue])

  const toggleDropdown = () => {
    if (!disabled) {
      setOpen((prev) => !prev)
    }
  }

  const handleSelectTime = (isStart: boolean, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!disabled) {
      setIsSelectingStartTime(isStart)
      setOpen(true)
    }
  }

  const renderTimeSegment = (
    time: string,
    placeholder: string,
    onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void
  ) => (
    <span className={`${range && css.timeSegmentRange} ${time ? css.timeValue : css.placeholder}`} onClick={onClick}>
      {time || placeholder}
    </span>
  )

  const renderSelector = (isRange: boolean) => {
    if (isRange) {
      return (
        <>
          {renderTimeSegment(selectedStartTime, 'Start time', (e) => handleSelectTime(true, e))}
          <SwapRight className={css.iconWrapperSwap} />
          {renderTimeSegment(selectedEndTime, 'End time', (e) => handleSelectTime(false, e))}
        </>
      )
    }
    return renderTimeSegment(selectedStartTime, 'Select time')
  }

  const handleTimeChange = (time: string) => {
    if (isSelectingStartTime) {
      setSelectedStartTime(time)
    } else {
      setSelectedEndTime(time)
    }
    if (range) {
      onChange?.([selectedStartTime, selectedEndTime])
    } else {
      onChange?.(time)
    }
  }

  return (
    <div
      className={cn({ [css.wrapper]: true, [css.boxShadowItemCalendar]: type === 'default' })}
      ref={containerRef}
      onClick={(e) => e.stopPropagation()}
    >
      {type === 'default' && (
        <div
          className={`${css.inputTimePicker} ${disabled && css.disabled} ${open && css.focused}`}
          onClick={toggleDropdown}
        >
          <div className={css.containerValue}>{renderSelector(range)}</div>
          <div className={css.iconWrapper}>
            <ClockCircle />
          </div>
        </div>
      )}
      {type === 'itemCalendar' && <div className={css.valueItemCalendar}>{selectedStartTime}</div>}
      <TimeDropdown
        type={type}
        onChange={handleTimeChange}
        time={isSelectingStartTime ? selectedStartTime : selectedEndTime}
        toggleDropdown={toggleDropdown}
        range={range}
        open={open}
      />
    </div>
  )
}

export default TimePicker
